import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { ChevronDown, ChevronUp, Heart, Store, Book, Calendar, School, Image, Lightbulb, Shield } from 'lucide-react';

function Login() {
  const navigate = useNavigate();
  const { signIn, isAuthenticated, isLoading } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [showMoreInfo, setShowMoreInfo] = useState(false);

  // If already authenticated, redirect
  useEffect(() => {
    if (isAuthenticated) {
      // Get stored redirect URL or default to dashboard
      const redirectUrl = localStorage.getItem('redirectUrl') || '/dashboard';
      localStorage.removeItem('redirectUrl'); // Clear stored URL
      navigate(redirectUrl);
    }
  }, [isAuthenticated, navigate]);

  const handleLogin = async () => {
    try {
      setError(null);
      await signIn();
      // Redirect will happen automatically due to useEffect above
    } catch (error: any) {
      console.error('Login failed:', error);
      setError(error.message || 'Login failed. Please ensure you are using an authorized email address.');
    }
  };

  const toggleMoreInfo = () => {
    setShowMoreInfo(!showMoreInfo);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col py-12 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="max-w-4xl mx-auto mb-4 text-center">
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
          Welcome to JLS Apps
        </h1>
        <p className="text-lg text-gray-600 dark:text-gray-400">
          A comprehensive suite of productivity and business tools designed for JLS Trading Co employees
        </p>
      </div>

      {/* Login Card - Moved above the app cards */}
      <Card className="max-w-md mx-auto w-full space-y-6 p-6 dark:bg-gray-800 mb-8">
        <div className="flex flex-col items-center">
          <img 
            src="/JLS_Apps_Logo.png" 
            alt="JLS Apps Logo" 
            className="h-16 w-16 mb-2"
          />
          <h2 className="mt-2 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
            JLS Apps
          </h2>
        </div>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded relative" role="alert">
            <span className="block sm:inline">{error}</span>
          </div>
        )}

        <div>
          <button
            onClick={handleLogin}
            disabled={isLoading}
            className={`group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white ${
              isLoading 
                ? 'bg-blue-400 dark:bg-blue-500 cursor-not-allowed' 
                : 'bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-blue-400'
            }`}
          >
            {isLoading ? 'Signing in...' : 'Sign in with Google'}
          </button>
        </div>

        <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-md">
          <div className="flex items-center">
            <Shield className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-2" />
            <p className="text-xs text-gray-600 dark:text-gray-400">
              Access is restricted to authorized JLS Trading Co employees. Please use your company Google account to sign in.
            </p>
          </div>
        </div>

        {/* Legal links in the login card */}
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <p className="text-center text-xs text-gray-500 dark:text-gray-400">
            <Link to="/privacy-policy" className="text-blue-600 hover:text-blue-800 dark:text-blue-400 hover:underline">
              Privacy Policy
            </Link>
            <span className="mx-2">•</span>
            <Link to="/cookie-policy" className="text-blue-600 hover:text-blue-800 dark:text-blue-400 hover:underline">
              Cookie Policy
            </Link>
            <span className="mx-2">•</span>
            <Link to="/terms-of-service" className="text-blue-600 hover:text-blue-800 dark:text-blue-400 hover:underline">
              Terms of Service
            </Link>
          </p>
        </div>
      </Card>
      
      {/* App Ecosystem Overview */}
      <div className="max-w-4xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-left mb-8">
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm">
            <div className="flex items-center mb-2">
              <Heart className="h-5 w-5 text-pink-500 mr-2" />
              <h3 className="font-semibold text-gray-900 dark:text-white">Good Things Tracker</h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Track and celebrate team achievements and recognition
            </p>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm">
            <div className="flex items-center mb-2">
              <Store className="h-5 w-5 text-blue-500 mr-2" />
              <h3 className="font-semibold text-gray-900 dark:text-white">Listing Management System</h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Optimize and manage product listings across multiple marketplaces
            </p>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm">
            <div className="flex items-center mb-2">
              <Book className="h-5 w-5 text-green-500 mr-2" />
              <h3 className="font-semibold text-gray-900 dark:text-white">SOP Central</h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Access and manage Standard Operating Procedures
            </p>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm">
            <div className="flex items-center mb-2">
              <Calendar className="h-5 w-5 text-cyan-500 mr-2" />
              <h3 className="font-semibold text-gray-900 dark:text-white">DayAway Board</h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Plan and track team time off for better work-life balance
            </p>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm">
            <div className="flex items-center mb-2">
              <School className="h-5 w-5 text-emerald-500 mr-2" />
              <h3 className="font-semibold text-gray-900 dark:text-white">PLMS</h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Personal Learning Management System for tracking educational progress
            </p>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm">
            <div className="flex items-center mb-2">
              <Image className="h-5 w-5 text-indigo-500 mr-2" />
              <h3 className="font-semibold text-gray-900 dark:text-white">Ideogram PromptStorm</h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Create and manage AI-generated images with Ideogram
            </p>
          </div>
        </div>
        
        {/* Technology Stack */}
        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg mb-6">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Secure & Modern Technology</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Built with React, Firebase, and Google Authentication for a secure, responsive, and real-time experience.
            Data is synchronized between Google Sheets and Firebase for reliable storage and fast access.
          </p>
        </div>
        
        {/* Learn More Toggle Button */}
        <button 
          onClick={toggleMoreInfo}
          className="flex items-center mx-auto text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 mb-4"
        >
          {showMoreInfo ? (
            <>
              <ChevronUp className="h-4 w-4 mr-1" />
              Show Less
            </>
          ) : (
            <>
              <ChevronDown className="h-4 w-4 mr-1" />
              Learn More
            </>
          )}
        </button>
      </div>
      
      {/* Expanded Information Section */}
      {showMoreInfo && (
        <div className="max-w-4xl mx-auto mt-6 mb-8 bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">About JLS Apps</h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Platform Features</h3>
              <ul className="list-disc pl-5 text-gray-600 dark:text-gray-400 space-y-1">
                <li>Single sign-on with Google Authentication</li>
                <li>Real-time data updates and synchronization</li>
                <li>Responsive design for desktop and mobile devices</li>
                <li>Dark mode support for reduced eye strain</li>
                <li>Role-based access control for secure data management</li>
                <li>Efficient data pagination for handling large datasets</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Benefits for Employees</h3>
              <ul className="list-disc pl-5 text-gray-600 dark:text-gray-400 space-y-1">
                <li>Centralized access to all JLS productivity tools</li>
                <li>Improved team collaboration and communication</li>
                <li>Streamlined workflows and processes</li>
                <li>Better visibility into team achievements and recognition</li>
                <li>Enhanced product management and optimization</li>
                <li>Simplified time-off planning and coordination</li>
              </ul>
            </div>
          </div>
        </div>
      )}
      
      {/* Footer with Policy links - Using the same footer as in AppHub */}
      <footer className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 border-t border-gray-200 dark:border-gray-700 mt-auto">
        <div className="text-center text-sm text-gray-500 dark:text-gray-400">
          <Link to="/privacy-policy" className="text-blue-600 hover:text-blue-800 dark:text-blue-400 hover:underline">
            Privacy Policy
          </Link>
          <span className="mx-2">•</span>
          <Link to="/cookie-policy" className="text-blue-600 hover:text-blue-800 dark:text-blue-400 hover:underline">
            Cookie Policy
          </Link>
          <span className="mx-2">•</span>
          <Link to="/terms-of-service" className="text-blue-600 hover:text-blue-800 dark:text-blue-400 hover:underline">
            Terms of Service
          </Link>
          <span className="mx-2">•</span>
          <span>© {new Date().getFullYear()} JLS Trading Co. All rights reserved.</span>
        </div>
      </footer>
    </div>
  );
}

export default Login;
